"use client";

import React from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Wind, Cloud, Armchair, ShieldPlus, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const solutions = [{
  title: "Temporary Power Systems",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648950/img/temporary_power_grid.png",
  icon: Wind,
  description: "Robust site power solutions for construction and industrial phases.",
  benefits: ["Heavy-duty distribution boards", "Weatherproof site lighting", "Full safety compliance testing"]
}, {
  title: "Industrial Containment",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648950/img/industrial_wiring_containment.png",
  icon: Cloud,
  description: "Precision-engineered cable management and containment systems.",
  benefits: ["Galvanised steel trunking", "High-capacity tray systems", "Future-proof grid layouts"]
}, {
  title: "Distribution Infrastructure",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648950/img/electrical_distribution_boards.png",
  icon: Armchair,
  description: "Main switchgear and sub-distribution board installations.",
  benefits: ["Load balancing optimization", "Circuit protection systems", "Detailed technical schematics"]
}, {
  title: "Site Groundwork Prep",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1648950/img/electrical_groundwork_prep.png",
  icon: ShieldPlus,
  description: "Foundational electrical infrastructure for large-scale builds.",
  benefits: ["Underground ducting & cabling", "Earthing & bonding systems", "Rigorous site safety audits"]
}];
export default function OurAdvancedTechnicalSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="228509" id="our-technical-capabilities" className="py-24 bg-background relative overflow-hidden" dir="ltr">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] rounded-none bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-none bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground font-default mb-4 tracking-tight uppercase">
            Our Technical Capabilities
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            GRIDSPAN INSTALLATIONS LTD delivers high-precision electrical infrastructure, ensuring your site is powered for performance and safety.
          </p>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {solutions.map((solution, i) => <div key={i} data-index={i} className="group relative bg-card rounded-none border border-border overflow-hidden transition-all duration-500 hover:shadow-xl hover:-translate-y-1 hover:border-primary flex flex-col">
              {}
              <div className="relative overflow-hidden">
                <AspectRatio ratio={4 / 3}>
                  <img src={solution.image} alt={solution.title} className="object-cover w-full h-full grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent opacity-80" />
                </AspectRatio>
                
                {}
                <div className="absolute top-4 left-4 bg-primary p-3 rounded-none shadow-md text-primary-foreground transform transition-all duration-300 group-hover:scale-110 group-hover:bg-accent group-hover:text-primary">
                  <solution.icon className="w-6 h-6" />
                </div>
              </div>

              {}
              <div className="p-6 flex-1 flex flex-col bg-card relative z-10">
                <h3 className="text-xl font-bold text-foreground font-default mb-2 uppercase tracking-wider">
                  {solution.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 font-default">
                  {solution.description}
                </p>

                {}
                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 ease-in-out mt-auto">
                  <div className="overflow-hidden">
                    <div className="pt-4 border-t border-border">
                      <ul className="space-y-3">
                        {solution.benefits.map((benefit, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 text-sm text-foreground/90 font-mono tracking-tight">
                            <CircleCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span>{benefit}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>
                
                {}
                <div className="mt-4 flex justify-center group-hover:opacity-0 transition-opacity duration-300">
                  <div className="w-12 h-0.5 rounded-none bg-primary" />
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}